import * as React from "react";
import { useState, useEffect, useMemo } from "react";
import Image from "next/image";
import { shimmer, toBase64 } from "@src/utils/shimmer";
import Dialog from "src/components/Dialog";
import Link from "next/link";
import Button from "@src/components/Button";
import { GetAlbumDetailsQuery } from "@src/@types/api.d";

type Props = {
  eventId: number;
  albumDetailsData: GetAlbumDetailsQuery;
  eventSlug: string;
};

export function insertAt(
  array: unknown[],
  index: number,
  ...elementsArray: unknown[]
) {
  array.splice(index, 0, ...elementsArray);
}

const Masonry: React.FC<Props> = ({ eventId, eventSlug, albumDetailsData }) => {
  // keep original media array
  const mediaData = React.useMemo(
    () => albumDetailsData?.getMedia || [],
    [albumDetailsData]
  );

  // only keep image media (same approach as template6/10)
  const imageMedia = React.useMemo(
    () =>
      (mediaData || []).filter(
        (m) =>
          m &&
          typeof m?.type === "string" &&
          m.type.toLowerCase().includes("image")
      ),
    [mediaData]
  );

  const isValidImageUrl = (img: any): boolean => {
    return (
      img &&
      img.meta &&
      typeof img.meta.s3_url === "string" &&
      img.meta.s3_url.trim() !== "" &&
      img.meta.s3_url.startsWith("http")
    );
  };

  const randomImages = useMemo(() => {
    const validMedia = (imageMedia || []).filter(isValidImageUrl);

    if (validMedia.length === 0) return [];
    if (validMedia.length <= 3) return validMedia;

    const shuffled = [...validMedia].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 3);
  }, [imageMedia]);

  if (!imageMedia || imageMedia.length === 0 || randomImages.length === 0) {
    return null;
  }

  return (
    <div className="bg-[#F9F6EC] lg:pt-[8rem] pt-[5rem] pb-[3rem] px-4 relative">
      <div className="max-w-6xl mx-auto">
        {/* MOBILE */}
        <div className="lg:hidden">
          <div className="text-center mb-8">
            <h2 className="text-5xl font-allura text-[#3E4F3C] mb-4">
              Picture Perfect Love
            </h2>
            <p className="text-lg font-lato text-[#3E4F3C]/80 mb-6 leading-relaxed max-w-md mx-auto">
              Every photograph tells the story of how deeply we've fallen.
            </p>
          </div>

          <div className="space-y-6 mb-8">
            {randomImages[0] && (
              <div className="w-full md:w-[75%] mx-auto">
                <div className="relative w-full aspect-[3/4] cursor-pointer rounded-2xl transition-all duration-500 transform hover:scale-[1.02]">
                  <Image
                    placeholder="blur"
                    blurDataURL={`data:image/svg+xml;base64,${toBase64(
                      shimmer(700, 475)
                    )}`}
                    src={randomImages[0]?.meta?.s3_url || ""}
                    layout="fill"
                    alt="Gallery image 1"
                    objectFit="cover"
                    className="rounded-2xl object-center shadow-2xl"
                    onError={(e) => {
                      console.error(
                        "Image load error:",
                        randomImages[0]?.meta?.s3_url
                      );
                      e.currentTarget.style.display = "none";
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl" />
                </div>
              </div>
            )}

            {randomImages[1] && (
              <div className="w-full md:w-[75%] mx-auto">
                <div className="relative w-full aspect-[3/4] cursor-pointer rounded-2xl transition-all duration-500 transform hover:scale-[1.02]">
                  <Image
                    placeholder="blur"
                    blurDataURL={`data:image/svg+xml;base64,${toBase64(
                      shimmer(700, 475)
                    )}`}
                    src={randomImages[1]?.meta?.s3_url || ""}
                    layout="fill"
                    alt="Gallery image 2"
                    objectFit="cover"
                    className="rounded-2xl object-center shadow-2xl"
                    onError={(e) => {
                      console.error(
                        "Image load error:",
                        randomImages[1]?.meta?.s3_url
                      );
                      e.currentTarget.style.display = "none";
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl" />
                </div>
              </div>
            )}

            {randomImages[2] && (
              <div className="w-full md:w-[60%] mx-auto">
                <div className="relative w-full aspect-[4/3] cursor-pointer rounded-2xl transition-all duration-500 transform hover:scale-[1.02]">
                  <Image
                    placeholder="blur"
                    blurDataURL={`data:image/svg+xml;base64,${toBase64(
                      shimmer(700, 475)
                    )}`}
                    src={randomImages[2]?.meta?.s3_url || ""}
                    layout="fill"
                    alt="Gallery image 3"
                    objectFit="cover"
                    className="rounded-2xl object-center shadow-2xl"
                    onError={(e) => {
                      console.error(
                        "Image load error:",
                        randomImages[2]?.meta?.s3_url
                      );
                      e.currentTarget.style.display = "none";
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl" />
                </div>
              </div>
            )}
          </div>

          {eventSlug && (
            <div className="text-center">
              <Link
                href={`https://${eventSlug}.${process.env.NEXT_PUBLIC_INAWO_ALBUM_SITE_END}`}
                passHref
              >
                <a target="_blank" rel="noopener noreferrer">
                  <button
                    className="px-8 py-3 rounded-full transition-all duration-300 border-2 border-[#3E4F3C] text-[#3E4F3C] text-xl font-corGaramond font-bold hover:bg-[#3E4F3C] hover:text-white transform hover:scale-105 shadow-lg hover:shadow-xl backdrop-blur-sm"
                    type="button"
                  >
                    See Full Photobook
                  </button>
                </a>
              </Link>
            </div>
          )}
        </div>

        {/* LARGE SCREENS */}
        <div className="hidden lg:flex flex-col lg:flex-row gap-8 lg:gap-12 items-center lg:items-stretch lg:justify-center">
          <div className="w-full md:w-[75%] lg:w-[30%] flex justify-center">
            {randomImages[0] && (
              <div className="relative w-full aspect-[3/4] cursor-pointer rounded-2xl transition-all duration-500 transform hover:scale-[1.02]">
                <Image
                  placeholder="blur"
                  blurDataURL={`data:image/svg+xml;base64,${toBase64(
                    shimmer(700, 475)
                  )}`}
                  src={randomImages[0]?.meta?.s3_url || ""}
                  layout="fill"
                  alt="Gallery image 1"
                  objectFit="cover"
                  className="rounded-2xl object-center shadow-2xl"
                  onError={(e) => {
                    console.error(
                      "Image load error:",
                      randomImages[0]?.meta?.s3_url
                    );
                    e.currentTarget.style.display = "none";
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl" />
              </div>
            )}
          </div>

          <div className="w-full lg:w-[40%] flex flex-col justify-center items-center text-center px-4">
            <h2 className="text-5xl lg:text-7xl font-allura text-[#3E4F3C] mb-4">
              Picture Perfect Love
            </h2>
            <p className="text-lg font-lato text-[#3E4F3C]/80 mb-6 leading-relaxed max-w-md">
              Every photograph tells the story of how deeply we've fallen.
            </p>

            {eventSlug && (
              <Link
                href={`https://${eventSlug}.${process.env.NEXT_PUBLIC_INAWO_ALBUM_SITE_END}`}
                passHref
              >
                <a target="_blank" rel="noopener noreferrer">
                  <button
                    className="px-8 py-3 rounded-full transition-all duration-300 border-2 border-[#3E4F3C] text-[#3E4F3C] text-xl font-corGaramond font-bold hover:bg-[#3E4F3C] hover:text-white transform hover:scale-105 shadow-lg hover:shadow-xl backdrop-blur-sm"
                    type="button"
                  >
                    See Full Photobook
                  </button>
                </a>
              </Link>
            )}
          </div>

          <div className="w-full md:w-[75%] lg:w-[30%] flex justify-center">
            {randomImages[1] && (
              <div className="relative w-full aspect-[3/4] cursor-pointer rounded-2xl transition-all duration-500 transform hover:scale-[1.02]">
                <Image
                  placeholder="blur"
                  blurDataURL={`data:image/svg+xml;base64,${toBase64(
                    shimmer(700, 475)
                  )}`}
                  src={randomImages[1]?.meta?.s3_url || ""}
                  layout="fill"
                  alt="Gallery image 2"
                  objectFit="cover"
                  className="rounded-2xl object-center shadow-2xl"
                  onError={(e) => {
                    console.error(
                      "Image load error:",
                      randomImages[1]?.meta?.s3_url
                    );
                    e.currentTarget.style.display = "none";
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl" />
              </div>
            )}
          </div>
        </div>

        <div className="hidden lg:block">
          {randomImages[2] && (
            <div className="flex justify-center mt-8">
              <div className="w-full md:w-[60%] lg:w-[40%]">
                <div className="relative w-full aspect-[4/3] cursor-pointer rounded-2xl transition-all duration-500 transform hover:scale-[1.02]">
                  <Image
                    placeholder="blur"
                    blurDataURL={`data:image/svg+xml;base64,${toBase64(
                      shimmer(700, 475)
                    )}`}
                    src={randomImages[2]?.meta?.s3_url || ""}
                    layout="fill"
                    alt="Gallery image 3"
                    objectFit="cover"
                    className="rounded-2xl object-center shadow-2xl"
                    onError={(e) => {
                      console.error(
                        "Image load error:",
                        randomImages[2]?.meta?.s3_url
                      );
                      e.currentTarget.style.display = "none";
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl" />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Masonry;
